import Dialog from "@/v2-ui/dialog"
import Button from "@/v2-backoffice-client/components/button"
import { JsonDiffEditor } from "@/v2-backoffice-client/components/editor"
import type { LayerAiFieldSuggestion } from "@/v2-backoffice-client/layer/ai/layer.ai.types"

export type LayerAiDiffDialogProps = {
  /** The field being reviewed; null keeps the dialog closed. */
  field: string | null
  suggestions: LayerAiFieldSuggestion[]
  /** The diff base: editor draft for Configuration, saved value otherwise. */
  getCurrentValue(field: string): string
  isSaving?: boolean
  onSelectField(field: string): void
  onApply(suggestion: LayerAiFieldSuggestion): void
  onClose(): void
}

/**
 * The suggestion review dialog: current value on the left, the assistant's
 * replacement on the right, with tabs when several fields carry
 * suggestions. Apply lands Configuration in the editor draft and saves the
 * other blobs immediately.
 */
function LayerAiDiffDialog(props: LayerAiDiffDialogProps) {
  const {
    field,
    suggestions,
    getCurrentValue,
    isSaving,
    onSelectField,
    onApply,
    onClose,
  } = props

  const suggestion = suggestions.find((candidate) => candidate.Field === field)

  return (
    <Dialog
      open={field !== null}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <Dialog.Content className="max-w-[90vw]">
        <Dialog.Header>
          <Dialog.Title>Suggested {field}</Dialog.Title>
          <Dialog.Description>
            {field === "Configuration"
              ? "Current editor draft on the left, the suggestion on the right. Apply replaces the editor content (undo works as usual)."
              : "Current saved value on the left, the suggestion on the right. Save writes it to the layer immediately."}
          </Dialog.Description>
        </Dialog.Header>

        {suggestions.length > 1 && (
          <div className="flex gap-1">
            {suggestions.map((candidate) => (
              <Button
                key={candidate.Field}
                size="tiny"
                variant={candidate.Field === field ? "primary" : "outline"}
                data-feature-code="UI_BackofficeLayers_AiAssistant_DiffTab"
                onClick={() => onSelectField(candidate.Field)}
              >
                {candidate.Field}
              </Button>
            ))}
          </div>
        )}

        <div className="h-[60vh] overflow-hidden rounded-md border">
          <JsonDiffEditor
            original={field ? getCurrentValue(field) : ""}
            modified={suggestion?.Value ?? ""}
          />
        </div>

        <Dialog.Footer>
          <Button
            variant="ghost"
            data-feature-code="UI_BackofficeLayers_AiAssistant_DiffClose"
            onClick={onClose}
          >
            Close
          </Button>
          <Button
            isLoading={isSaving}
            data-feature-code="UI_BackofficeLayers_AiAssistant_DiffApply"
            onClick={() => {
              if (suggestion) onApply(suggestion)
            }}
          >
            {field === "Configuration" ? "Apply to editor" : "Save"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default LayerAiDiffDialog
